import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { DateDescriptions, useOverviewContext } from '@insights_module/context/OverviewContext';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { getDateNumberDaysAgo, roundDateToNearestHour } from '@utils/dateUtils';
import { Loader } from '@components/Loader';
import { useOverviewData } from '@app/hooks/useOverviewData';

const StyledOption = tw(Option)`text-[14px] text-text-and-icon font-medium`;
const Wrapper = tw.div`flex flex-col sm:flex-row justify-start w-full px-[24px] py-[8px] gap-[8px]`;
const InfoWrapper = tw.div`flex items-center`;
const Info = tw.div`flex items-center bg-info-bg sm:h-[28px] px-[12px] rounded text-[14px] text-text-and-icon font-normal`;

const timeRangeOptions: IDropdownOption[] = [
  {
    value: 'last_7_days',
    label: (
      <StyledOption>
        <Trans i18nKey='insights.overview_page.date_picker_selection.last_7_days' />
      </StyledOption>
    ),
    meta: { days: '7' },
  },
  {
    value: 'last_30_days',
    label: (
      <StyledOption>
        <Trans i18nKey='insights.overview_page.date_picker_selection.last_30_days' />
      </StyledOption>
    ),
    meta: { days: '30' },
  },
  {
    value: 'last_60_days',
    label: (
      <StyledOption>
        <Trans i18nKey='insights.overview_page.date_picker_selection.last_60_days' />
      </StyledOption>
    ),
    meta: { days: '60' },
  },
  {
    value: 'last_90_days',
    label: (
      <StyledOption>
        <Trans i18nKey='insights.overview_page.date_picker_selection.last_90_days' />
      </StyledOption>
    ),
    meta: { days: '90' },
  },
  // for now we are not using all time option because named entities should be cleaned up for the older episodes
  // {
  //   value: 'all_time',
  //   label: (
  //     <StyledOption>
  //       <Trans i18nKey='insights.overview_page.date_picker_selection.all_time' />
  //     </StyledOption>
  //   ),
  // },
];

type ResultType = 'episodes' | 'categories' | 'entities' | 'none';

export const DateIntervalSelection = ({
  resultType = 'episodes',
  className,
}: {
  resultType?: ResultType;
  className?: string;
}) => {
  const { t } = useTranslation();
  const { selectedDateRange, setSelectedDateRange, setSelectedTrendingDateRange } =
    useOverviewContext();
  const {
    episodesCount,
    isLoadingEpisodeCount,
    rankedCategories,
    isLoadingCategories,
    rankedNamedEntities,
    isLoadingNamedEntities,
  } = useOverviewData();
  const [selectedOption, setSelectedOption] = useState<IDropdownOption>(
    timeRangeOptions.find((option) => option.value === selectedDateRange.description) ||
      timeRangeOptions[0]
  );

  const isLoading = useMemo(() => {
    if (resultType === 'episodes') {
      return isLoadingEpisodeCount;
    }

    if (resultType === 'categories') {
      return isLoadingCategories;
    }

    return isLoadingNamedEntities;
  }, [isLoadingEpisodeCount, isLoadingCategories, isLoadingNamedEntities, resultType]);

  useEffect(() => {
    if (!selectedOption) return;

    if (selectedOption.value === 'all_time') {
      setSelectedDateRange({
        description: selectedOption.value as DateDescriptions,
        minDate: null,
        maxDate: null,
      });

      setSelectedTrendingDateRange({
        description: selectedOption.value as DateDescriptions,
        minDate: null,
        maxDate: null,
      });

      return;
    }

    setSelectedDateRange({
      description: selectedOption.value as DateDescriptions,
      minDate: roundDateToNearestHour(getDateNumberDaysAgo(Number(selectedOption?.meta?.days))),
      maxDate: roundDateToNearestHour(new Date()),
    });

    setSelectedTrendingDateRange({
      description: selectedOption.value as DateDescriptions,
      // set min and max date so date range is the same as selected date range but with offset of selected date range
      minDate: roundDateToNearestHour(
        getDateNumberDaysAgo(Number(selectedOption?.meta?.days) * 2 + 1)
      ),
      maxDate: roundDateToNearestHour(getDateNumberDaysAgo(Number(selectedOption?.meta?.days) + 1)),
      days: Number(selectedOption?.meta?.days),
    });
  }, [selectedOption]);

  const info = useMemo(() => {
    if (!episodesCount) {
      return t('insights.overview_page.date_picker_selection.episode_selected_info_no_episodes');
    }

    if (resultType === 'episodes') {
      return t('insights.overview_page.date_picker_selection.episode_selected_info', {
        episodesCount: episodesCount.toLocaleString(),
      });
    }

    if (resultType === 'categories') {
      return t('insights.overview_page.date_picker_selection.trending_result', {
        result: rankedCategories.length.toLocaleString(),
      });
    }

    return t('insights.overview_page.date_picker_selection.trending_result', {
      result: rankedNamedEntities.length.toLocaleString(),
    });
  }, [episodesCount, rankedCategories, rankedNamedEntities, resultType]);

  return (
    <Wrapper className={className}>
      <Dropdown
        options={timeRangeOptions}
        value={selectedOption}
        onChange={(val) => setSelectedOption(val as IDropdownOption)}
        labelStyle={tw`bg-neutral-background border border-gray-1 text-text-and-icon font-medium shadow-none w-[130px]`}
        caretStyle={tw`fill-text-and-icon`}
        dropdownMenuStyle={tw`w-full text-text-and-icon`}
      />

      {resultType !== 'none' && (
        <InfoWrapper>
          {isLoading ? (
            <Loader size='sm' theme='dark' />
          ) : (
            <Info css={[resultType !== 'episodes' && tw`bg-transparent`]}>{info} </Info>
          )}
        </InfoWrapper>
      )}
    </Wrapper>
  );
};
